/**
 * ActivityCard — vertical timeline of the user's account events.
 *
 * Section content only (no outer Card). Owned by the unified Profile
 * card; sibling sections sit above/below with hairline dividers.
 *
 * Data sources today:
 *   - `previousSignInAt` is the prior sign-in (the `issued_at` of the
 *     most recent session other than the current one). Null on the very
 *     first session ever — we collapse the row in that case rather than
 *     show an em-dash. We deliberately do NOT use `lastSeenAt` here:
 *     `verifyOtp` touches it to the *current* sign-in, so it would read
 *     "just now" and duplicate the Active-session row.
 *   - `createdAt` is the "Account created" marker — always present.
 *
 * V1.x will add: profile-edited events (from the audit log via
 * `identity.profileActivity`), avatar-changed, recovery-rotated, etc.
 * The component is shaped around an `events: TimelineEvent[]` array so
 * adding rows is mechanical when the wire surface lands.
 */
import * as React from "react";
import type { MeUser } from "../../lib/session.ts";
import { formatDateTime, formatRelativeTime } from "../../lib/format.ts";

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  at: string;
  /** Whether this event is the "active" / most recent — dot is filled + pulses. */
  active?: boolean;
}

export interface ActivityCardProps {
  user: MeUser;
}

export function ActivityCard({ user }: ActivityCardProps) {
  // Recompute relative times every minute so the chip stays honest
  // without us hand-rolling a tick. UI-only refresh, no network.
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    const id = window.setInterval(force, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const events: TimelineEvent[] = [
    {
      id: "session",
      title: "Active session",
      description: "You are signed in on this device",
      at: new Date().toISOString(),
      active: true,
    },
    ...(user.previousSignInAt
      ? [
          {
            id: "last-seen",
            title: "Last sign-in",
            description: formatDateTime(user.previousSignInAt),
            at: user.previousSignInAt,
          },
        ]
      : []),
    {
      id: "created",
      title: "Account created",
      description: "Welcome to Flint",
      at: user.createdAt,
    },
  ];

  return (
    <section className="px-5 sm:px-6 py-5 sm:py-6 flex flex-col gap-5">
      <header>
        <h2 className="text-sm font-semibold text-ink uppercase tracking-wider">Activity</h2>
        <p className="text-sm text-muted mt-1">Recent account events.</p>
      </header>

      <ol className="relative flex flex-col gap-5">
        {/* Continuous connector line behind the dots. */}
        <span aria-hidden className="absolute left-[11px] top-1.5 bottom-1.5 w-px bg-line" />
        {events.map((event) => (
          <TimelineRow key={event.id} event={event} />
        ))}
      </ol>
    </section>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  return (
    <li className="relative flex items-start gap-4">
      <span className="relative shrink-0 w-[22px] flex justify-center pt-1">
        {event.active ? (
          <span className="absolute inset-0 grid place-items-center">
            <span className="h-2 w-2 rounded-full bg-sky-600" />
            <span className="absolute h-3.5 w-3.5 rounded-full bg-sky-100 -z-10 animate-pulse" />
          </span>
        ) : (
          <span className="h-2 w-2 rounded-full bg-paper border border-line-2" />
        )}
      </span>

      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-ink">{event.title}</span>
          <span className="text-sm text-muted shrink-0">{formatRelativeTime(event.at)}</span>
        </div>
        <span className="text-sm text-muted">{event.description}</span>
      </div>
    </li>
  );
}
